import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { Box, Button, Card, CardContent, Rating, Typography, Grid } from '@mui/material';
import { StarBorder as StarBorderIcon } from '@mui/icons-material';
import { Labeler } from 'react-text-annotator';
import { useTranslation } from 'react-i18next';
import { useGetReviewsAnnotationsTagsQuery } from '../features/api/rest/apiReviewsAnnotationsSlice';
import Error from './Error';
import Spinner from './Spinner';

function ProductOrganizationReviewCard({ ean, id, date, title, description, rating, owner }) {
  const formatDate = (dateToFormat) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateToFormat).toLocaleDateString('it-IT', options);
  };
  const { t } = useTranslation();
  const [annotations, setAnnotations] = useState([]);
  const [completeAnnotations, setCompleteAnnotations] = useState([]);
  const [buttonSelected, setButtonSelected] = useState('');
  const [uniqueTypes, setUniqueTypes] = useState([]);

  const colorEnum = {
    positive: 'green',
    negative: 'red',
  };

  const { data: tags = [], isLoading, isFetching, isError } = useGetReviewsAnnotationsTagsQuery({ ean, rpk: id });

  useEffect(() => {
    if (tags) {
      const tmpAnnotations = [];
      const tmpUniqueType = [];
      tags.forEach((tag) => {
        if (tag.attributes.length > 0) {
          tag.attributes.forEach((attribute) => {
            tmpAnnotations.push({ id: tag.id, color: colorEnum[attribute.value], endToken: tag.end - 1, startToken: tag.start, name: attribute.value, type: attribute.name, kind: 'label' });
            if (tmpUniqueType.indexOf(attribute.name) === -1) {
              tmpUniqueType.push(attribute.name);
            }
          });
        }
      });
      setButtonSelected(tmpAnnotations[1]?.type);
      setCompleteAnnotations(tmpAnnotations);
      setUniqueTypes(tmpUniqueType);
      const tmp = tmpAnnotations.filter((annotation) => {
        return annotation.type === tmpAnnotations[1]?.type;
      });
      setAnnotations(tmp);
    }
  }, [tags]);

  const changeTag = (type) => {
    if (completeAnnotations) {
      const tmp = completeAnnotations.filter((annotation) => {
        return annotation.type === type;
      });
      setAnnotations(tmp);
      setButtonSelected(type);
    }
  };

  const checkVariant = (type) => {
    if (buttonSelected === type) return 'contained';
    return 'text';
  };

  const buttonSelection = uniqueTypes.map((annotationType) => {
    return (
      <Grid item key={annotationType}>
        <Button type="button" variant={checkVariant(annotationType)} onClick={() => changeTag(annotationType)}>
          {annotationType}
        </Button>
      </Grid>
    );
  });

  if (isLoading || isFetching) return <Spinner removeBox />;

  if (isError) return <Error />;

  return (
    <Card sx={{ border: '1px solid #d3d4d5', my: 4 }} elevation={0} data-testid="ReviewCard">
      <CardContent>
        <Typography sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
          {owner}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Rating name="read-only" value={rating} readOnly emptyIcon={<StarBorderIcon />} />
          <Typography variant="h6" component="div" sx={{ ml: 2, fontSize: 17, fontWeight: 'bold' }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: 13 }}>
          {formatDate(date)}
        </Typography>
        <Labeler text={description} annotations={annotations} labelerConfigs={{ isDisabled: true }} />
        {annotations.length === 0 && t('reviews.noAnnotations')}
        <Grid container spacing={1} sx={{ mt: 2 }}>
          {buttonSelection}
        </Grid>
      </CardContent>
    </Card>
  );
}

ProductOrganizationReviewCard.propTypes = {
  ean: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  date: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  rating: PropTypes.number.isRequired,
  owner: PropTypes.string.isRequired,
};

export default ProductOrganizationReviewCard;
